import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useImage } from '../../hooks/useImage';
import useCustomNotification from '../../hooks/useCustomNotification';

/**
 * ReferenceImageGeneratorDialog Component
 * 
 * Dialog that allows users to generate images based on reference images
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {Array<Object>} props.referenceImages - Array of selected reference images
 * @param {string} props.projectId - Optional project ID for saving generated images
 */
const ReferenceImageGeneratorDialog = ({ 
  open, 
  onClose, 
  referenceImages = [],
  projectId
}) => {
  const { 
    loading, 
    generatedImages, 
    generateFromReferences, 
    saveImageToProject,
    clearGeneratedImages 
  } = useImage();
  
  const { showNotification } = useCustomNotification();
  
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState(null);
  const [options, setOptions] = useState({
    model: 'gpt-image-1', // Only GPT Image 1 supports reference images
    size: '1024x1024',
    n: 1
  });

  // Maximum number of reference images allowed
  const MAX_REFERENCES = 4;
  
  // Filter reference images if more than the maximum are provided
  const validReferenceImages = referenceImages.slice(0, MAX_REFERENCES);

  // Handle prompt input changes
  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
  };

  // Handle option changes
  const handleOptionChange = (key, value) => {
    setOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle generating image from references
  const handleGenerateImage = async () => {
    if (!prompt.trim()) {
      setError('Please enter a description for the image you want to generate');
      return;
    }
    
    if (validReferenceImages.length === 0) {
      setError('No reference images selected');
      return;
    }
    
    setError(null);
    
    try {
      // Extract image objects with URLs from the reference images
      const referenceImageObjects = validReferenceImages.map(img => ({
        url: img.url
      }));
      
      // Generate the new image using the references
      await generateFromReferences(prompt, referenceImageObjects, options);
      
      showNotification({
        message: 'Image generation started - this may take up to a minute',
        type: 'info'
      });
    } catch (error) {
      console.error('Failed to generate image from references:', error);
      
      // Handle common errors
      if (error.response) {
        // Server responded with an error status
        const status = error.response.status;
        const errorData = error.response.data || {};
        
        if (status === 401) {
          setError("API key authentication failed. Please check your OpenAI API key.");
        } else if (status === 400) {
          setError(errorData.message || "Bad request. Please check your prompt and try again.");
        } else if (status === 429) {
          setError("Rate limit exceeded. Please try again later.");
        } else {
          setError(errorData.message || "An error occurred while generating the image.");
        }
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }
    }
  };

  // Handle saving an image to the project
  const handleSaveImage = async (imageUrl) => {
    if (!projectId) return;
    
    setError(null);
    
    try {
      const savedImage = await saveImageToProject(projectId, imageUrl, prompt, {
        model: options.model,
        fromReferences: true,
        referenceCount: validReferenceImages.length
      });
      
      showNotification({
        message: 'Image saved successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to save image:', error);
      
      if (error.response) {
        const errorData = error.response.data || {};
        setError(errorData.message || "Failed to save image.");
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred while saving the image.");
      }
    }
  };

  // Handle dialog close with cleanup
  const handleClose = () => {
    if (onClose) {
      // Clean up when dialog is closed
      clearGeneratedImages();
      setPrompt('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Generate Image Using References
        </Typography>
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2, p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Generate a new image using your selected references as inspiration.
          </Typography>
          
          {/* Reference Images Preview */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Reference Images ({validReferenceImages.length})
          </Typography>
          
          <Box sx={{ mb: 3, p: 1, backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
            <Grid container spacing={2}>
              {validReferenceImages.map((image, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Paper elevation={1} sx={{ p: 0.5, position: 'relative' }}>
                    <img
                      src={image.url}
                      alt={`Reference ${index + 1}`}
                      style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
        
        {error && (
          <Box sx={{ mb: 2 }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: 'error.light', 
                color: 'error.dark',
                borderRadius: 1
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Paper>
          </Box>
        )}
        
        {/* Prompt Input */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Image Description
          </Typography>
          <TextField
            fullWidth
            placeholder="Describe the image you want to generate using the reference images..."
            value={prompt}
            onChange={handlePromptChange}
            multiline
            rows={3}
            variant="outlined"
            disabled={loading}
          />
        </Box>
        
        {/* Options */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Size</InputLabel>
              <Select
                value={options.size}
                onChange={(e) => handleOptionChange('size', e.target.value)}
                label="Size"
                disabled={loading}
              >
                <MenuItem value="1024x1024">1024x1024 (Square)</MenuItem>
                <MenuItem value="1024x1792">1024x1792 (Portrait)</MenuItem>
                <MenuItem value="1792x1024">1792x1024 (Landscape)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Quantity</InputLabel>
              <Select
                value={options.n}
                onChange={(e) => handleOptionChange('n', e.target.value)}
                label="Quantity"
                disabled={loading}
              >
                {[1, 2, 3, 4].map(num => (
                  <MenuItem key={num} value={num}>{num}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {/* Generate Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerateImage}
            disabled={loading || !prompt.trim()}
            startIcon={loading && <CircularProgress size={20} color="inherit" />}
            sx={{ textTransform: 'none' }}
          >
            {loading ? 'Generating...' : 'Generate image from references'}
          </Button>
          
          {generatedImages.length > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={clearGeneratedImages}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              Clear Results
            </Button>
          )}
        </Box>
        
        {/* Generated Images */}
        {generatedImages.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" gutterBottom fontWeight={500}>
              Generated Images
            </Typography>
            
            <Grid container spacing={2}>
              {generatedImages.map((img, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper elevation={2} sx={{ p: 1, position: 'relative' }}>
                    {img.processing ? (
                      // Placeholder for processing image
                      <Box 
                        sx={{ 
                          width: '100%', 
                          height: '200px', 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0,0,0,0.04)',
                          borderRadius: '4px'
                        }}
                      >
                        <CircularProgress 
                          size={40} 
                          thickness={4} 
                          sx={{ mb: 2 }}
                          variant={img.progress && img.progress > 0 ? "determinate" : "indeterminate"} 
                          value={img.progress || 0}
                        />
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ px: 2 }}>
                          {img.message || "Generating image with references..."}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          {img.progress && img.progress > 0 
                            ? `${Math.round(img.progress)}% complete`
                            : "This may take up to a minute"}
                        </Typography>
                        {img.requestId && (
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            mt: 1, 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            color: 'primary.main'
                          }} className="streaming-indicator">
                            <span className="streaming-dot"></span>
                            Streaming image in progress...
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      // Regular image display
                      <img 
                        src={img.url} 
                        alt={`Generated image ${index + 1}`}
                        style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }}
                      />
                    )}
                    
                    {projectId && img.url && !img.processing && (
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSaveImage(img.url)}
                          disabled={loading}
                          sx={{ textTransform: 'none' }}
                        >
                          Save
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReferenceImageGeneratorDialog;